import React, { useCallback, useContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

import { Context } from 'components/Store';
import { selectCropSeason } from 'utilities/updated-permissions';

import usePortal from 'hooks/usePortal';
import OrganizationImage from 'components/Icons/components/OrganizationImage';
import {
  GLOBAL_SET_ORGANIZATION,
  GLOBAL_SET_ORGANIZATIONS,
  IS_GLOBAL_CROPSEASON_LOADING,
  RESET_MASS_ASSIGNER
} from 'reducers/reducer';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import createAction from 'helpers/createAction';
import useOrganizations from 'hooks/useOrganizations';
import { AddRounded } from '@material-ui/icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';
import useCropSeasonsListApi from 'hooks/useCropSeasonsListApi';
import BreadCrumbItem from './BreadCrumbItem';
import BreadcrumbDropDown from '../../../../Navigation/components/BreadcrumbDropDown';
import BreadcrumbCropSeasons from '../../../../Navigation/components/BreadcrumbCropSeasons';
import CreateOrganizationModal from '../../../../../screens/Organizations/components/CreateOrganizationModal';

const BREADCRUMB_PORTAL_DOM_ID = 'cwf-breadcrumb-portal';

const breadcrumbDefaultPropOnOrganizationSelect = () => {};
const breadcrumbDefaultPropOnCropSeasonSelect = () => {};
const Breadcrumb = ({
  children = /** @type {React.ReactNode} */ (null),
  hideOrganizationDropdown = false,
  hideCropSeasonDropdown = true,
  onOrganizationSelect = breadcrumbDefaultPropOnOrganizationSelect,
  onOrganizationConfirm = /** @type {((...args: any[]) => any) | undefined} */ (undefined),
  onOrganizationCreateConfirm = /** @type {((...args: any[]) => any) | undefined} */ (undefined),
  onCropSeasonConfirm = /** @type {((...args: any[]) => any) | undefined} */ (undefined),
  onCropSeasonCreateConfirm = /** @type {((...args: any[]) => any) | undefined} */ (undefined),
  onCropSeasonSelect = breadcrumbDefaultPropOnCropSeasonSelect,
  onCropSeasonValidation = /** @type {((...args: any[]) => any) | undefined} */ (undefined),
  disabled = false,
  disabledCropSeasons = false,
  disabledCropSeasonCreate = false,
  allowMultipleCropSeasonSelection = false,
  cropSeasonResetKey = 0
}) => {
  const [currentOrg, setCurrentOrg] = useState(null);
  const breadcrumbPortal = usePortal(BREADCRUMB_PORTAL_DOM_ID);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const { updateIdentify } = useContext(AmplitudeContext);
  const [{ organization, organizations, cropSeasons }, dispatch] = useContext(
    Context
  );
  const { getDefaultOrg, loadedDefaultOrg, defaultOrg } = useOrganizations();
  const openModal = () => setCreateModalOpen(true);
  const closeModal = () => setCreateModalOpen(false);

  const {
    releaseFeCondensedCropSeasons,
    releaseFeCondensedCropSeasonsByOrg
  } = useFlags();

  const isCondensedCropSeasonEnabled = featureFlagRenderItem(
    releaseFeCondensedCropSeasons,
    releaseFeCondensedCropSeasonsByOrg,
    organization?.id
  );
  const seasonsApi = useCropSeasonsListApi(isCondensedCropSeasonEnabled);

  const orgDropDownItemProps = {
    name: org => org.name,
    isActive: org => organization && organization.id === org.id
  };

  const getCropSeasons = useCallback(async () => {
    await selectCropSeason(
      dispatch,
      null,
      isCondensedCropSeasonEnabled,
      seasonsApi
    );
  }, [dispatch, isCondensedCropSeasonEnabled, seasonsApi]);

  const finalizeOrgSelection = selectedOrg => {
    onOrganizationSelect(selectedOrg);
    dispatch({ type: RESET_MASS_ASSIGNER });
    updateIdentify(selectedOrg?.name, selectedOrg?.id);
  };

  const handleOrgSelect = async org => {
    const selectOrgWrapper = async () => {
      localStorage.setItem('selectedCropSeasons', JSON.stringify([]));
      const shouldSyncCropSeasons = !hideCropSeasonDropdown;

      if (!shouldSyncCropSeasons) {
        createAction(dispatch, GLOBAL_SET_ORGANIZATION, org);
        createAction(dispatch, IS_GLOBAL_CROPSEASON_LOADING, false);
        finalizeOrgSelection(org);
        return;
      }

      // Keep loading flag ahead of org update so downstream hooks don't fetch with stale seasons.
      createAction(dispatch, IS_GLOBAL_CROPSEASON_LOADING, true);
      createAction(dispatch, GLOBAL_SET_ORGANIZATION, org);
      await getCropSeasons();
      finalizeOrgSelection(org);
    };

    if (onOrganizationConfirm) {
      onOrganizationConfirm(org, selectOrgWrapper);
      return;
    }

    await selectOrgWrapper();
  };

  const handleSeasonSelect = async seasons => {
    const selectSeasonsWrapper = async () => {
      return selectCropSeason(
        dispatch,
        seasons,
        isCondensedCropSeasonEnabled,
        seasonsApi
      );
    };
    if (onCropSeasonConfirm) {
      onCropSeasonConfirm(seasons, selectSeasonsWrapper);
      return;
    }

    await selectSeasonsWrapper();
    onCropSeasonSelect();
  };

  useEffect(() => {
    if (hideCropSeasonDropdown) {
      return;
    }
    getCropSeasons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideCropSeasonDropdown]);

  useEffect(() => {
    setCurrentOrg(organization);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  useEffect(() => {
    if (!defaultOrg) {
      return;
    }
    createAction(dispatch, GLOBAL_SET_ORGANIZATIONS, defaultOrg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOrg]);

  const defaultBreadcrumb = (
    <>
      <div className="flex w-full">{children}</div>
    </>
  );
  if (process.env.JEST_WORKER_ID) {
    return defaultBreadcrumb;
  }
  return ReactDOM.createPortal(
    <>
      <CreateOrganizationModal
        open={createModalOpen}
        handleCancel={closeModal}
        onSelectConfirm={onOrganizationConfirm}
      />
      <div className="flex w-full mr-6 gap-2">
        {hideOrganizationDropdown ? null : (
          <div
            id="breadcrumb-organization"
            data-testid="breadcrumb-organization"
            className="flex items-center"
          >
            <div className="mr-2">
              <OrganizationImage />
            </div>
            {!disabled ? (
              <BreadcrumbDropDown
                dropDownItemProps={orgDropDownItemProps}
                getData={getDefaultOrg}
                organizations={organizations}
                onSelect={handleOrgSelect}
                searchPlaceholder="Search Organizations"
                title="Organization"
                value={currentOrg?.name ?? 'New Organization'}
                disabled
                dropDownFooter={props => (
                  <button
                    data-testid="add-organization-button"
                    type="button"
                    onClick={event => {
                      event.stopPropagation();
                      // A dirty crop zone form has to block the create flow
                      // before it opens, not just the eventual switch to the
                      // org it creates — otherwise the org already exists in
                      // the backend by the time the guard shows up.
                      const openCreateModal = () => {
                        openModal();
                        props.onClick(event);
                      };
                      if (onOrganizationCreateConfirm) {
                        onOrganizationCreateConfirm(openCreateModal);
                      } else {
                        openCreateModal();
                      }
                    }}
                  >
                    <div className="text-cucumber-green-700  text-sm font-normal hover:text-cucumber-green-500">
                      <div className="w-auto flex flex-row items-center">
                        <AddRounded style={{ width: '30%' }} />
                        <p style={{ whiteSpace: 'nowrap' }}>
                          Create Organization
                        </p>
                      </div>
                    </div>
                  </button>
                )}
                isLast={!children}
                loadedOrgs={loadedDefaultOrg}
              />
            ) : (
              <div
                data-testid="nav-menu-head"
                className="flex items-center shrink-0 focus:border-none text-white"
              >
                <div>
                  <BreadCrumbItem
                    title="Organization"
                    value={currentOrg?.name ?? 'New Organization'}
                    isLast={!children}
                    disabled
                  />
                </div>
              </div>
            )}
          </div>
        )}
        {hideCropSeasonDropdown ? null : (
          <div
            id="breadcrumb-cropseason"
            data-testid="breadcrumb-cropseason"
            className="flex items-center"
          >
            <BreadcrumbCropSeasons
              key={cropSeasonResetKey}
              title="Crop Season"
              searchPlaceholder="Search crop seasons"
              getData={() => cropSeasons}
              onSelect={handleSeasonSelect}
              allowMultipleSelection={allowMultipleCropSeasonSelection}
              disabled={disabledCropSeasons}
              disabledCreate={disabledCropSeasonCreate}
              validationBeforeSet={onCropSeasonValidation}
              onCreateConfirm={onCropSeasonCreateConfirm}
            />
          </div>
        )}
        {children ?? defaultBreadcrumb}
      </div>
    </>,
    breadcrumbPortal
  );
};

Breadcrumb.Item = BreadCrumbItem;
Breadcrumb.DropDown = BreadcrumbDropDown;

Breadcrumb.propTypes = {
  children: PropTypes.node,
  hideOrganizationDropdown: PropTypes.bool,
  hideCropSeasonDropdown: PropTypes.bool,
  onOrganizationSelect: PropTypes.func,
  onOrganizationConfirm: PropTypes.func,
  onOrganizationCreateConfirm: PropTypes.func,
  onCropSeasonConfirm: PropTypes.func,
  onCropSeasonCreateConfirm: PropTypes.func,
  onCropSeasonSelect: PropTypes.func,
  onCropSeasonValidation: PropTypes.func,
  disabled: PropTypes.bool,
  disabledCropSeasons: PropTypes.bool,
  disabledCropSeasonCreate: PropTypes.bool,
  allowMultipleCropSeasonSelection: PropTypes.bool,
  cropSeasonResetKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default Breadcrumb;
